import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Table } from '@/components/ui/daisy/table';
import { getErrorMessage } from '@/utils/getErrorMessage';

import { Loading } from '../../loading';
import { resolveVisibleColumns } from './resolveVisibleColumns';
import { SmartTableProps } from './types';

export * from './types';

export function SmartTable<T, K extends string = string>({
  columns,
  order,
  data,
  getRowKey,
  isLoading = false,
  error,
  emptyMessage,
  classNames,
  ...tableProps
}: SmartTableProps<T, K>) {
  const visibleColumns = useMemo(
    () => resolveVisibleColumns(columns, order),
    [columns, order],
  );

  const isEmpty = !isLoading && !error && data.length === 0;

  return (
    <Table.Container className={classNames?.container}>
      <Table {...tableProps}>
        <Table.Head>
          <Table.Row>
            {visibleColumns.map(({ key, header, headerClassName }) => (
              <Table.HeaderCell
                key={key}
                className={twMerge(classNames?.header, headerClassName)}
              >
                {header}
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <ConditionalRenderer condition={isLoading}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <div className="flex w-full justify-center py-6">
                  <Loading color="primary" size="sm" />
                </div>
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={!!error}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <Text
                  format="rubik-400"
                  className="flex w-full justify-center py-6 text-neutral"
                  text={getErrorMessage(error)}
                />
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={isEmpty}>
            <Table.Row>
              <Table.Cell colSpan={visibleColumns.length}>
                <Text
                  format="rubik-400"
                  className="flex w-full justify-center py-6 text-neutral"
                  text={emptyMessage}
                />
              </Table.Cell>
            </Table.Row>
          </ConditionalRenderer>

          <ConditionalRenderer condition={!isLoading}>
            {data.map(item => (
              <Table.Row key={getRowKey(item)}>
                {visibleColumns.map(({ key, Cell, cellClassName }) => (
                  <Table.Cell key={key} className={cellClassName}>
                    <Cell item={item} />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </ConditionalRenderer>
        </Table.Body>
      </Table>
    </Table.Container>
  );
}
